import { DatePipe } from '@angular/common';
import { AfterContentChecked, AfterViewInit, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { CodelistDisplayModel } from '@common/models/common/CodelistDisplay.model';
import { GeneralActService } from '@common/services/general-act.service';
import { NominatedAgentService } from '@common/services/nominated-agent.service';
import { marker } from '@common/translate-marker';
import { maxLengthValidator } from '@common/validators/max-length-validator';
import { requiredValidator } from '@common/validators/required-validator';
import { TranslateService } from '@ngx-translate/core';
import { DialogRef } from '@progress/kendo-angular-dialog';
import { ToastService } from '@services/toast.service';
import { forkJoin } from 'rxjs';
import { GeneralActDisplayModel } from '../../models/GeneralAct.display.model';

@Component({
    selector: 'app-save-general-act-modal',
    templateUrl: './save-general-act-modal.component.html',
    styleUrls: ['./save-general-act-modal.component.scss'],
    standalone: false
})
export class SaveGeneralActModalComponent implements OnInit, AfterViewInit, AfterContentChecked {
    public title: string;
    vesselVisitId: number;
    agentId: string;
    vesselName: string;
    vesselVisitEta: Date;
    form: FormGroup;
    isBusy: boolean;
    editMode: boolean;
    // if we have generalAct, then it is for edit, otherwise it is for create
    generalAct: GeneralActDisplayModel;

    terminalOptions: CodelistDisplayModel[];
    vesselAgents: CodelistDisplayModel[];
    customMRNCodelist: CodelistDisplayModel[];
    nominatedAgents: any[];
    cargoTypes: CodelistDisplayModel[]; // had to go this way in order to avoid async issues

    // this is for the case when it is edit mode, to check if the mrnId is changed in order to update the number
    initialMrnId: number;

    constructor(
        public activeModal: DialogRef,
        private generalActService: GeneralActService,
        private toastyService: ToastService,
        private translateService: TranslateService,
        private nominatedAgentService: NominatedAgentService,
        private datePipe: DatePipe,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.form = this.createFormGroup();
        this.initializeModal();
    }

    ngAfterViewInit(): void {
        this.checkEditMode();
        this.trackFormChanges();
    }

    ngAfterContentChecked() {
        this.cdr.detectChanges();
    }

    saveGeneralAct() {
        this.generalActService
            .saveGeneralAct({ ...this.form.getRawValue(), vesselVisitId: this.vesselVisitId })
            .subscribe({
                next: (id: number) => {
                    this.toastyService.success(
                        this.translateService.instant(
                            this.generalAct
                                ? marker('Outturn Report saved successfully')
                                : marker('Outturn Report created successfully')
                        )
                    );
                    this.activeModal.close({ result: true, id: id });
                },
                error: () => {}
            });
    }

    private initializeModal() {
        this.isBusy = true;
        this.title = this.generalAct
            ? this.translateService.instant(marker('Edit Outturn Report'))
            : this.translateService.instant(marker('Create Outturn Report'));
        this.initialMrnId = this.generalAct?.mrnId;
        forkJoin({
            terminalOptions: this.generalActService.getTerminalOptions(this.vesselVisitId),
            vesselAgents: this.generalActService.getVesselAgents(),
            customMRNCodelist: this.generalActService.getGeneralActCustomMRNCodelist(this.vesselVisitId),
            cargoTypes: this.generalActService.getGeneralActCargoTypes(),
            nominatedAgents: this.nominatedAgentService.getNominatedAgents({ VesselVisitId: this.vesselVisitId })
        }).subscribe(({ terminalOptions, vesselAgents, customMRNCodelist, cargoTypes, nominatedAgents }) => {
            this.cargoTypes = cargoTypes as CodelistDisplayModel[];
            this.terminalOptions = terminalOptions as CodelistDisplayModel[];
            if (this.terminalOptions?.length === 1)
                this.form.patchValue({ relationSystemToOrganizationToTerminalId: this.terminalOptions[0].id });
            this.vesselAgents = vesselAgents as CodelistDisplayModel[];
            this.customMRNCodelist = customMRNCodelist as CodelistDisplayModel[];
            this.nominatedAgents = nominatedAgents.data;
            if (this.customMRNCodelist?.length === 1) this.form.patchValue({ mrnId: this.customMRNCodelist[0].id });

            const cargoTypeCodes = (this.nominatedAgents ?? []).map((x) => x?.cargoType?.localizedCode).filter(Boolean);

            const distinctCargoTypes = [...new Set(cargoTypeCodes)];

            if (distinctCargoTypes.length === 1) {
                const cargoType = this.cargoTypes?.find((x) => x.localizedCode === distinctCargoTypes[0]);

                if (cargoType) {
                    this.form.get('cargoTypeId')?.patchValue(cargoType.id);
                }
            }

            this.isBusy = false;
        });
    }

    private checkEditMode() {
        if (this.generalAct) {
            this.editMode = true;
            this.form.patchValue({
                ...this.generalAct,
                shippingLineId: this.generalAct.shippingLineId,
                relationSystemToOrganizationToTerminalId:
                    this.generalAct.relationSystemToOrganizationToTerminalId.toString()
            });
            ['relationSystemToOrganizationToTerminalId', 'cargoDirectionId'].forEach((x) => this.form.get(x).disable());
        }
    }

    private createFormGroup() {
        const form = new FormGroup({
            id: new FormControl(null),
            relationSystemToOrganizationToTerminalId: new FormControl(null, requiredValidator()),
            shippingLineId: new FormControl(+this.agentId, requiredValidator()),
            cargoDirectionId: new FormControl('I', requiredValidator()),
            mrnId: new FormControl(null),
            number: new FormControl(null, requiredValidator()),
            name: new FormControl(''),
            remark: new FormControl(null),
            vesselRepresentative: new FormControl(null, maxLengthValidator(50)),
            terminalRepresentative: new FormControl(null, maxLengthValidator(50)),
            customsRepresentative: new FormControl(null, maxLengthValidator(50)),
            operationsStartDate: new FormControl(null),
            operationsEndDate: new FormControl(null),
            cargoTypeId: new FormControl(null, requiredValidator())
        });
        form.patchValue(this as any);
        return form;
    }

    private trackFormChanges() {
        /*if (!this.editMode) {
            this.form.get('relationSystemToOrganizationToTerminalId').valueChanges.subscribe((value) => {
                if (value) {
                    this.generalActService.checkVesselVisitCargoType(this.vesselVisitId, value).subscribe({
                        next: (result) => {
                            if (!result) return;
                            if (result.forRoro) {
                                this.form.get('cargoTypeId').patchValue('RORO');
                            } else if (result.forContainers) {
                                this.form.get('cargoTypeId').patchValue('CONT');
                            } else {
                                this.form.get('cargoTypeId').patchValue('BULK');
                            }
                        }
                    });
                }
            });
        }*/

        this.form.get('mrnId').valueChanges.subscribe((value) => {
            if (value && value !== this.initialMrnId?.toString()) {
                const mrnValue = this.customMRNCodelist.find((x) => x.id === value);
                this.form.get('number').patchValue(mrnValue?.name);
            }
        });
    }
}
